import { faClose } from '@fortawesome/pro-light-svg-icons';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Select, SelectProps, Space, Tag, TreeSelect } from 'antd';
import { DefaultOptionType } from 'antd/es/select';
import { MultiSelectStyle } from 'components/commons/MultiSelect/styles';
import { uniq, uniqBy } from 'lodash';
import { useState } from 'react';
import { DynamicObjectType } from 'types/commons';

export interface MultiSelectProps extends SelectProps {
  handleChange?: (
    value: string | number | string[] | DynamicObjectType | DynamicObjectType[],
    option?: DefaultOptionType | DefaultOptionType[],
  ) => void;
  selected?: DynamicObjectType[];
  onClose?: (value: string | number | DynamicObjectType) => void;
  showInSideInput?: boolean;
  isTreeSelect?: boolean;
  parentProp?: string;
  selectedData?: DynamicObjectType[];
  onSearch?: (value: string) => void;
  onFilterNotIsSelected?: (value: boolean) => void;
  mode?: 'multiple' | 'tags';
}

type TagRender = SelectProps['tagRender'];

export default function MultiSelect({
  placeholder,
  options,
  handleChange,
  selected,
  onClose,
  showInSideInput,
  isTreeSelect,
  selectedData,
  parentProp,
  onSearch,
  onFilterNotIsSelected,
  mode = 'multiple',
  ...props
}: MultiSelectProps) {
  const tagRender: TagRender = (props) => {
    const { label, closable, value } = props;
    const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
      event.preventDefault();
      event.stopPropagation();
    };
    return (
      <Tag
        onMouseDown={onPreventMouseDown}
        closable={closable}
        onClose={() => {
          onClose(value);
          props.onClose();
        }}
        className="ant-tag-primary mr-2.5"
      >
        {label}
      </Tag>
    );
  };

  const customFilterOption = (inputValue: string, option) => {
    if (!option || !option.label || option.disabled) return false;

    const optionLabel = option?.label as string;
    return optionLabel
      .trim()
      .toLowerCase()
      .includes(inputValue.trim().toLowerCase());
  };

  const filterData = (value: string | number) => {
    return selectedData?.find((e) => e?.id === value);
  };

  const renderTreeData = (value: DynamicObjectType) => {
    if (!value?.[parentProp]) {
      return (
        <div>
          {value?.detail?.[0]?.name ||
            value?.name ||
            value?.label ||
            'Waiting to update'}
        </div>
      );
    } else if (value?.[parentProp]) {
      return (
        <Space className="d-flex" size={10} wrap>
          {renderTreeData(value?.[parentProp])}
          <FontAwesomeIcon icon={faChevronRight} className="text-highlight" />
          <>
            {value?.detail?.[0]?.name ||
              value?.name ||
              value?.label ||
              'Waiting to update'}
          </>
        </Space>
      );
    }
    return null;
  };
  // console.log(selected);
  const getExpandedKeys = (data, expandedKeys = []) => {
    data.forEach((node) => {
      if (node.children) {
        expandedKeys.push(node.value);
        expandedKeys = getExpandedKeys(node.children, expandedKeys);
      }
    });
    return expandedKeys;
  };
  const [selectedKeys, setSelectedKeys] = useState(getExpandedKeys(options));

  const handleTreeExpand = (expandedKeys) => {
    setSelectedKeys(expandedKeys);
  };

  function findItemById(array, id) {
    for (const item of array) {
      if (item.value === id) {
        return item;
      }
      if (item.children && item.children.length > 0) {
        const foundInChildren = findItemById(item.children, id);
        if (foundInChildren) {
          return foundInChildren;
        }
      }
    }
    return null; // Item not found
  }

  function filterItems(items, idsToFilter) {
    const valueSet = new Set(idsToFilter.map((obj) => obj.value));

    return items.reduce((filtered, item) => {
      if (!valueSet.has(item.value)) {
        const filteredChildren = item.children
          ? filterItems(item.children, idsToFilter)
          : [];
        filtered.push({ ...item, children: filteredChildren });
      } else if (item.children) {
        const filteredChildren = filterItems(item.children, idsToFilter);
        if (filteredChildren.length > 0) {
          filtered.push({ ...item, children: filteredChildren });
        }
      }
      return filtered;
    }, []);
  }

  if (isTreeSelect) {
    const filteredItems = filterItems(options, selected);
    return (
      <MultiSelectStyle>
        <TreeSelect
          showSearch
          style={{ width: '100%' }}
          dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
          placeholder={placeholder}
          allowClear
          multiple
          treeDefaultExpandAll
          onChange={(value) => {
            const optionItem = findItemById(filteredItems, value[0]);
            if (optionItem?.children?.length > 0) {
              setSelectedKeys((prev) => uniq([...prev, value?.[0]]));
              return;
            } else {
              handleChange(value);
            }
          }}
          treeExpandedKeys={selectedKeys}
          onTreeExpand={handleTreeExpand}
          treeData={filteredItems}
          tokenSeparators={['&nbsp;']}
          value={[]}
          onSearch={onSearch}
          filterTreeNode={() => true}
          onDropdownVisibleChange={(open) => {
            if (!open) {
              onSearch('');
            }
          }}
        />
        <Space wrap className="mt-2.5">
          {uniqBy(selected, 'value')?.map(
            (e: DynamicObjectType, key: number) => (
              <Tag
                className="ant-tag-primary d-flex fw-600"
                closable
                onClose={() => {
                  onClose(e?.value);
                }}
                key={key}
              >
                {renderTreeData(filterData(e?.value))}
              </Tag>
            ),
          )}
        </Space>
      </MultiSelectStyle>
    );
  }

  return (
    <MultiSelectStyle>
      {showInSideInput ? (
        <Select
          mode={mode}
          onChange={(e) => {
            const uniqueValues = uniqBy(e, (item) =>
              String(item).trim().toLowerCase(),
            );
            const itemSelect = uniqueValues?.map((i) =>
              options?.find(
                (o) =>
                  String(o.value).toLowerCase().trim() ===
                    String(i).toLowerCase().trim() ??
                  String(o.label).toLowerCase().trim() ===
                    String(i).toLowerCase().trim(),
              )
                ? options.find(
                    (o) =>
                      String(o.value).toLowerCase().trim() ===
                        String(i).toLowerCase().trim() ??
                      String(o.label).toLowerCase().trim() ===
                        String(i).toLowerCase().trim(),
                  )?.value
                : i,
            );
            handleChange(uniq(itemSelect));
          }}
          placeholder={placeholder}
          options={options}
          optionFilterProp="label"
          tagRender={tagRender}
          size="small"
          value={selected}
          tokenSeparators={['&nbsp;']}
          filterOption={customFilterOption}
          {...props}
        />
      ) : (
        <>
          <Select
            mode="multiple"
            autoClearSearchValue={false}
            onChange={handleChange}
            placeholder={placeholder}
            options={options?.filter(
              (e) =>
                !selected.find((item) => `${item?.value}` === `${e.value}`),
            )}
            value={[] as string[]}
            onSearch={onSearch}
            filterOption={() => true}
            onDropdownVisibleChange={(open) => {
              if (!open) {
                onSearch('');
                if (selected?.length > 0) {
                  onFilterNotIsSelected(true);
                } else {
                  onFilterNotIsSelected(false);
                }
              }
            }}
          />
          <div className="tag-list">
            {selected?.map((e, key) => (
              <div key={key} className="custom-tag fw-600">
                {e?.label
                  ? e?.label?.$$typeof || typeof e?.label === 'string'
                    ? e?.label
                    : JSON.stringify(e?.label)
                  : JSON.stringify(e?.value)}
                <FontAwesomeIcon
                  icon={faClose}
                  className="text-xs tag-close-icon"
                  onClick={() => {
                    onClose(e.value);
                    if (selected?.length > 0) {
                      onFilterNotIsSelected(true);
                    } else {
                      onFilterNotIsSelected(false);
                    }
                  }}
                />
              </div>
            ))}
          </div>
        </>
      )}
    </MultiSelectStyle>
  );
}
